<template>
  <div class="booking-page" :style="themeStyle">
    <div v-if="!company" class="booking-page__loading">{{ $t("booking.loading") }}</div>

    <template v-else>
      <header class="booking-page__header">
        <img v-if="company.logo_url" :src="company.logo_url" class="booking-page__logo" alt="" />
        <div>
          <h1>{{ company.title }}</h1>
          <p v-if="company.location">{{ company.address }}</p>
          <p v-if="company.phone">{{ company.phone }}</p>
        </div>
      </header>

      <section v-if="!selectedService" class="booking-page__browse">
        <v-text-field
          v-model="search"
          :label="$t('booking.search_services')"
          density="comfortable"
          prepend-inner-icon="mdi-magnify"
          clearable
        />

        <div class="booking-page__categories">
          <v-chip
            :color="activeCategory === null ? 'primary' : undefined"
            @click="activeCategory = null"
          >
            {{ $t("booking.all_services") }}
          </v-chip>
          <v-chip
            v-for="category in categories"
            :key="category.id"
            :color="activeCategory === category.id ? 'primary' : undefined"
            @click="activeCategory = category.id"
          >
            {{ category.title }}
          </v-chip>
        </div>

        <div class="booking-page__services">
          <v-card
            v-for="service in filteredServices"
            :key="service.id"
            class="booking-page__service"
            @click="selectService(service)"
          >
            <v-card-title>{{ service.name }}</v-card-title>
            <v-card-subtitle>{{ service.duration }} min &middot; {{ service.price }}</v-card-subtitle>
            <v-card-text v-if="service.description">{{ service.description }}</v-card-text>
          </v-card>

          <p v-if="!filteredServices.length" class="booking-page__empty">
            {{ $t("booking.no_services_match") }}
          </p>
        </div>
      </section>

      <section v-else class="booking-page__flow">
        <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="selectedService = null">
          {{ $t("booking.back_to_services") }}
        </v-btn>

        <h2>{{ selectedService.name }}</h2>

        <AvailabilityCalendar
          v-if="!selectedTime"
          v-model="selectedTime"
          v-model:employee-id="selectedEmployeeId"
          :company-id="companyId"
          :service-id="selectedService.id"
          :timezone="company.timezone"
          :business-hours="company.business_hours"
          :custom-schedule="company.custom_schedule"
          :duration="selectedService.duration"
        />

        <template v-else-if="!identityReady">
          <v-tabs v-model="bookingMode" grow class="mb-4">
            <v-tab value="quick">{{ $t("booking.quick_booking_tab") }}</v-tab>
            <v-tab value="account">{{ $t("booking.account_booking_tab") }}</v-tab>
          </v-tabs>

          <WalkInBookingForm v-if="bookingMode === 'quick'" @continue="onWalkInSubmit" />
          <GuestAuthPanel v-else @authenticated="onAuthenticated" />

          <v-btn variant="text" class="mt-2" @click="resetTimeSelection">{{ $t("booking.pick_different_time") }}</v-btn>
        </template>

        <template v-else-if="guestStore.guestUser && !guestStore.guestUser.confirmed">
          <v-alert type="warning" density="comfortable">
            {{ $t("booking.verify_email_prompt") }}
            <v-btn variant="text" size="small" :loading="resending" @click="resendVerification">
              {{ $t("booking.resend_verification") }}
            </v-btn>
          </v-alert>
        </template>

        <BookingConfirmationCard
          v-else
          :company-id="companyId"
          :service="selectedService"
          :start-time="selectedTime"
          :employee-id="selectedEmployeeId"
          :customer="walkInCustomer"
          @change="resetTimeSelection"
        />
      </section>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import {
  getGuestProfile,
  getPublicCategories,
  getPublicCompany,
  getPublicServices,
  resendGuestVerification,
} from "../../services/public-booking-api.service";
import {
  PublicCategory,
  PublicCompany,
  PublicService,
  WalkInCustomerDTO,
} from "../../types/public-booking.type";
import { useGuestStore } from "../../stores/guest.store";
import AvailabilityCalendar from "./AvailabilityCalendar.vue";
import GuestAuthPanel from "./GuestAuthPanel.vue";
import WalkInBookingForm from "./WalkInBookingForm.vue";
import BookingConfirmationCard from "./BookingConfirmationCard.vue";

const route = useRoute();
const guestStore = useGuestStore();

const companyId = computed(() => route.params.companyId as string);

const company = ref<PublicCompany | null>(null);
const services = ref<PublicService[]>([]);
const categories = ref<PublicCategory[]>([]);
const search = ref("");
const activeCategory = ref<number | null>(null);

const selectedService = ref<PublicService | null>(null);
const selectedTime = ref<string | null>(null);
// Lifted out of AvailabilityCalendar (rather than read directly from it)
// since that component gets torn down once a time slot is picked
// (`v-if="!selectedTime"` above) - by the time BookingConfirmationCard
// needs it, AvailabilityCalendar and its own local state are already gone.
const selectedEmployeeId = ref<number | null>(null);
const resending = ref(false);

const bookingMode = ref<"quick" | "account">("quick");
const walkInCustomer = ref<WalkInCustomerDTO | null>(null);
const identityReady = computed(
  () => !!guestStore.guestUser || !!walkInCustomer.value
);

const themeStyle = computed(() =>
  company.value?.theme_color ? { "--booking-accent": company.value.theme_color } : {}
);

const filteredServices = computed(() => {
  return services.value.filter((service) => {
    const matchesCategory = activeCategory.value === null || service.category_id === activeCategory.value;
    const matchesSearch =
      !search.value || service.name.toLowerCase().includes(search.value.toLowerCase());
    return matchesCategory && matchesSearch;
  });
});

const selectService = (service: PublicService) => {
  selectedService.value = service;
  resetTimeSelection();
};

const resetTimeSelection = () => {
  selectedTime.value = null;
  selectedEmployeeId.value = null;
};

const onAuthenticated = () => {
  // Booking form re-renders reactively once guestStore.guestUser is set.
};

const onWalkInSubmit = (customer: WalkInCustomerDTO) => {
  walkInCustomer.value = customer;
};

const resendVerification = async () => {
  if (!guestStore.guestUser) return;
  resending.value = true;
  try {
    await resendGuestVerification(guestStore.guestUser.email);
  } catch {
    // The global axios interceptor already surfaces the server's error message.
  } finally {
    resending.value = false;
  }
};

onMounted(async () => {
  company.value = await getPublicCompany(companyId.value);
  const [servicesResponse, categoryList] = await Promise.all([
    getPublicServices(companyId.value, { limit: 50 }),
    getPublicCategories(companyId.value),
  ]);
  services.value = servicesResponse.services;
  categories.value = categoryList;

  // The cached guestUser (persisted across visits) can be stale — e.g. `confirmed`
  // flips to true on a separate /verify-email/:token page — so refresh it here.
  if (guestStore.guestUser) {
    try {
      guestStore.setGuestUser(await getGuestProfile());
    } catch {
      guestStore.setGuestUser(null);
    }
  }
});
</script>

<style lang="scss" scoped>
@use "../../styles/_breakpoints" as bp;

.booking-page {
  --booking-accent: #1976d2;
  max-width: 960px;
  margin: 40px auto;
  padding: 32px;
  background: #fff;
  border-radius: 20px;

  &__loading {
    padding: 60px;
    text-align: center;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;

    @include bp.large-phone {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
  }

  &__logo {
    width: 64px;
    height: 64px;
    border-radius: 8px;
    object-fit: cover;
  }

  &__categories {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 12px 0;

    @include bp.large-phone {
      flex-wrap: nowrap;
      overflow-x: auto;
    }
  }

  &__services {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }

  &__service {
    cursor: pointer;
    border-top: 3px solid var(--booking-accent);
  }

  &__empty {
    color: #888;
  }

  @include bp.tablet {
    margin: 0;
    padding: 20px 16px;
    border-radius: 0;

    &__services {
      grid-template-columns: 1fr;
    }
  }
}
</style>
